"use client"

import type React from "react"
import { useState, useMemo, useRef, useEffect } from "react"
import { Card, CardBody, Button, FormGroup, Label, Alert, Spinner } from "reactstrap"
import type { Exhibitor } from "./types"
import { FaBuilding, FaChevronDown, FaSearch , FaArrowRight} from "react-icons/fa"

// Enhanced Exhibitor Selection Dropdown Component (Compact)
interface ExhibitorSelectionDropdownProps {
  exhibitors: Exhibitor[]
  onExhibitorSelected: (exhibitor: Exhibitor) => void
  loading?: boolean
}

const ExhibitorSelectionDropdown: React.FC<ExhibitorSelectionDropdownProps> = ({ exhibitors, onExhibitorSelected, loading = false }) => {
  const [selectedExhibitor, setSelectedExhibitor] = useState<Exhibitor | null>(null)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const filteredExhibitors = useMemo(() => {
    if (!searchTerm.trim()) return exhibitors

    const searchLower = searchTerm.toLowerCase()
    return exhibitors.filter(
      (exhibitor) =>
        exhibitor.name.toLowerCase().includes(searchLower) 
        
    )
  }, [exhibitors, searchTerm])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setIsOpen(true)
  }

  const handleExhibitorSelect = (exhibitor: Exhibitor) => {
    setSelectedExhibitor(exhibitor)
    setSearchTerm(exhibitor.name)
    setIsOpen(false)
  }

  const handleConfirmSelection = () => {
    if (selectedExhibitor) {
      onExhibitorSelected(selectedExhibitor)
    }
  }

  const toggleDropdown = () => {
    setIsOpen(!isOpen)
  }

  if (exhibitors.length === 0) {
    return (
      <Card className="h-100 border-0 shadow-sm">
        <CardBody className="d-flex flex-column justify-content-center p-4 text-center">
          <div className="bg-light rounded-circle d-inline-flex p-3 mb-3">
            <FaBuilding className="text-muted" size={20} />
          </div>
          <h6 className="fw-semibold">No Companies Available</h6>
          <p className="text-muted small mb-0">
            There are currently no companies available for selection. Please contact the administrator.
          </p>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card className="h-100 border-0 shadow-sm">
      <CardBody className="p-3 d-flex flex-column">
        <div className="text-center mb-3">
          <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex p-2 mb-2">
            <FaBuilding className="text-primary" size={16} />
          </div>
          <h6 className="fw-semibold mb-1">Select Company</h6>
          <p className="text-muted small mb-0">
            Search and choose company to verify and access the dashboard.
          </p>
        </div>

        <FormGroup className="mb-3">
          <Label className="fw-medium mb-1 small">Company *</Label>
          <div className="position-relative" ref={dropdownRef}>
            <div
              className={`form-control d-flex justify-content-between align-items-center rounded ${isOpen ? "border-primary" : "border"}`}
              style={{ cursor: "pointer", minHeight: "38px", padding: "6px 24px", fontSize: '1rem' }}
              onClick={toggleDropdown}
            >
              <input
                type="text"
                className="border-0 bg-transparent flex-grow-1 outline-0"
                style={{ outline: "none", fontSize: '1rem' }}
                placeholder="Search companies..."
                value={searchTerm}
                onChange={handleSearchChange}
                onClick={(e) => e.stopPropagation()}
              />
              <FaChevronDown
                className={`text-muted transition-all ${isOpen ? "rotate-180" : ""}`}
                size={12}
              />
            </div>

            {isOpen && (
              <div
                className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm mt-1"
                style={{ zIndex: 1000, maxHeight: "240px", overflowY: "auto", fontSize: '1rem' }}
              >
                {filteredExhibitors.length > 0 ? (
                  filteredExhibitors.map((exhibitor) => (
                    <div
                      key={exhibitor.id}
                      className="p-2 border-bottom cursor-pointer"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleExhibitorSelect(exhibitor)}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      <div className="fw-medium" style={{fontSize: '1rem'}}>{exhibitor.name}</div>
                      {/* {exhibitor.email && (
                        <div className="text-muted small mt-1">{exhibitor.email}</div>
                      )} */}
                    </div>
                  ))
                ) : (
                  <div className="p-3 text-center text-muted small">
                    <FaSearch className="mb-1 opacity-50" />
                    <div>No companies found</div>
                  </div>
                )}
              </div>
            )}
          </div>
          {searchTerm && !isOpen && filteredExhibitors.length > 0 && (
            <div className="text-muted small mt-1">
              Found {filteredExhibitors.length} companies matching "{searchTerm}"
            </div>
          )}
        </FormGroup>

        {selectedExhibitor && (
          <Alert color="info" className="border-0 bg-info py-2 px-3 mb-3">
            <div className="d-flex align-items-center">
              <div className="flex-grow-1">
                <span className="fw-medium small text-dark">Selected:</span>
                <div className="fw-semibold text-dark" style={{fontSize: '1rem'}}>{selectedExhibitor.name}</div>
              </div>
            </div>
          </Alert>
        )}

        <div className="text-center mt-auto pt-2">
          <Button 
            color="primary" 
            className="px-3 py-1 fw-medium rounded" 
            onClick={handleConfirmSelection} 
            disabled={!selectedExhibitor || loading}
            style={{fontSize: '0.85rem'}}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-1" />
                Verifying...
              </>
            ) : (
              <>
                Continue <FaArrowRight className="ms-1" size={12} />
              </>
            )}
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}

export default ExhibitorSelectionDropdown
